import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, User } from 'lucide-react';
import { useAuth } from '../lib/AuthContext';

function initialsFromUser(user: { displayName?: string | null; email?: string | null } | null) {
  const name = (user?.displayName || '').trim();
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  }
  const email = (user?.email || '').trim();
  if (email) return email.slice(0, 2).toUpperCase();
  return '';
}

export const UserAvatar: React.FC<{
  size?: number;
  className?: string;
}> = ({ size = 36, className = '' }) => {
  const { user } = useAuth();
  const [imgFailed, setImgFailed] = useState(false);
  const photoURL = user?.photoURL || null;
  const initials = initialsFromUser(user);

  useEffect(() => {
    setImgFailed(false);
  }, [photoURL]);

  if (photoURL && !imgFailed) {
    return (
      <img
        src={photoURL}
        alt=""
        width={size}
        height={size}
        className={`kin-avatar-img ${className}`.trim()}
        style={{ width: size, height: size }}
        onError={() => setImgFailed(true)}
        referrerPolicy="no-referrer"
      />
    );
  }

  if (initials) {
    return (
      <span
        className={`kin-avatar-fallback ${className}`.trim()}
        style={{ width: size, height: size, fontSize: Math.max(11, Math.round(size * 0.36)) }}
        aria-hidden
      >
        {initials}
      </span>
    );
  }

  return (
    <span
      className={`kin-avatar-fallback kin-avatar-icon ${className}`.trim()}
      style={{ width: size, height: size }}
      aria-hidden
    >
      <User size={Math.round(size * 0.55)} />
    </span>
  );
};

type AccountMenuProps = {
  /** Compact chip for studio/dark headers */
  variant?: 'default' | 'studio';
};

export const AccountMenu: React.FC<AccountMenuProps> = ({ variant = 'default' }) => {
  const { user, logout, isDevAuth } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    close();
  }, [location.pathname, close]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        close();
        buttonRef.current?.focus();
      }
    };
    const onPointer = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node | null;
      if (rootRef.current && target && !rootRef.current.contains(target)) close();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('touchstart', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('touchstart', onPointer);
    };
  }, [open, close]);

  if (!user) return null;

  const displayName = user.displayName || user.email || 'Account';
  const email = user.email || '';

  return (
    <div className={`kin-account-menu${variant === 'studio' ? ' kin-account-menu-studio' : ''}`} ref={rootRef}>
      <button
        ref={buttonRef}
        type="button"
        className="kin-account-menu-trigger"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-controls={menuId}
        aria-label="Account menu"
        onClick={() => setOpen((v) => !v)}
      >
        <UserAvatar size={34} />
        <ChevronDown className={`kin-account-menu-chevron${open ? ' is-open' : ''}`} aria-hidden />
      </button>

      <div
        id={menuId}
        role="menu"
        className={`kin-account-menu-panel${open ? ' is-open' : ''}`}
        aria-hidden={!open}
      >
        <div className="kin-account-menu-identity">
          <UserAvatar size={40} />
          <div className="kin-account-menu-identity-text">
            <strong>{displayName}</strong>
            {email ? <span>{email}</span> : null}
          </div>
        </div>
        <div className="kin-account-menu-links">
          <Link role="menuitem" to="/app/account" onClick={close}>
            Account
          </Link>
          <Link role="menuitem" to="/app/library" onClick={close}>
            Library
          </Link>
          <Link role="menuitem" to="/app/account#billing" onClick={close}>
            Billing &amp; credits
          </Link>
          <Link role="menuitem" to="/app/account#connected" onClick={close}>
            Google Photos
          </Link>
        </div>
        {!isDevAuth && (
          <div className="kin-account-menu-footer">
            <button
              type="button"
              role="menuitem"
              className="kin-account-menu-signout"
              onClick={() => {
                close();
                logout();
              }}
            >
              Sign out
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
